import {
  each,
  isArray,
  isBoolean,
  isDate,
  isNumber,
  isObject,
  isString,
  omit,
  reduce,
  size,
  uniqueId,
} from "lodash";
import { isMoment } from "@tootools/date";
import { isCardWithRequest } from "@radarly/models/cards/is";
import { isCardRow } from "@radarly/models/insight-pages/row";
import { PublicPage } from "@radarly/models/public-pages";
import {
  SearchData,
  SearchFragment,
  SearchParams,
} from "@radarly/models/search";
import { SearchRepository } from "./SearchRepository";

const reduceEmptyValues = (values) => {
  return reduce(
    values,
    (acc, value, key: string) => {
      if (
        isString(value) ||
        isBoolean(value) ||
        isNumber(value) ||
        isDate(value) ||
        isMoment(value) ||
        ((isObject(value) || isArray(value)) && size(value))
      ) {
        acc[key] = value;
      }
      return acc;
    },
    {}
  );
};

const orderKeys = (unordered: object): object => {
  const ordered = {};
  Object.keys(reduceEmptyValues(unordered))
    .sort()
    .forEach((key) => {
      const value = unordered[key];
      if (
        isString(value) ||
        isBoolean(value) ||
        isNumber(value) ||
        isArray(value) ||
        isDate(value) ||
        isMoment(value)
      ) {
        ordered[key] = value;
      } else if (isObject(value)) {
        ordered[key] = orderKeys(value);
      }
    });
  return ordered;
};

export class PublicSearchRepository implements SearchRepository {
  private data: Record<string, SearchData> = {};
  constructor(publicPage: PublicPage) {
    each(publicPage.customPage.rows, (row) => {
      if (isCardRow(row)) {
        each(row.cards, (card) => {
          if (card && isCardWithRequest(card)) {
            each(card.fragments, (fragment: SearchFragment) => {
              fragment.request.id = uniqueId("query_");
              this.data[this.getKeyFromQuery(fragment.request)] = fragment.data;
              this.data[fragment.request.id] = fragment.data;
            });
          }
        });
      }
    });
  }
  private getKeyFromQuery(query: any) {
    return JSON.stringify(orderKeys(omit(query, "name", "timezone")));
  }
  async searchFragmentData<T extends SearchData>(
    project: number,
    query: SearchParams,
    mutualized?: boolean
  ) {
    if (query.id) {
      return (this.data[query.id] || null) as T;
    } else {
      return (this.data[this.getKeyFromQuery(query)] || null) as T;
    }
  }
}
